import { Box } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PhoneInput from '../PhoneInput';
import { _renderStringAsFlag } from 'pages/tpm/';

const PhoneInputCell = ({ getValue, row, column, table, addRow = true }) => {
    const initValue = getValue();
    const { darkMode } = useSelector((state) => state.settings);
    const {
        focusedRow,
        setFocusedRow,
        numberCopy,
        setNumberCopy,
        updateData,
        updateTable,
        deleteRow,
        spin,
        type,
    } = table.options.meta;
    const [value, setValue] = useState(initValue);
    const numberRef = useRef(null);
    const isPastingRef = useRef(false);

    const onBlur = () => {
        // Skip updateData if we just pasted - updateTable already handled the data
        if (isPastingRef.current) {
            isPastingRef.current = false;
            setNumberCopy(false);
            return;
        }
        if (value !== initValue) {
            updateData(row.index, column.id, value);
        }
        // Reset paste mode when user leaves the field
        if (numberCopy) {
            setNumberCopy(false);
        }
    };

    const addNewRow = () => {
        updateData(row.index, column.id, value, addRow);
        setFocusedRow((fr) => fr + 1);
    };

    const onPaste = (event) => {
        const pastedData = event.clipboardData.getData('text');
        if (!pastedData || pastedData.length === 0) return;

        event.preventDefault();
        isPastingRef.current = true;
        setNumberCopy(true);

        const numbers = pastedData
            .split(/[\r\n;,]+/)
            .map((part) => part.replace(/\D/g, '').trim()) // Remove all non-digits (including +)
            .filter((num) => num.length > 0 && num.match(/^[1-9][0-9]+$/));

        if (numbers.length > 1) {
            // Bulk paste: join cleaned numbers with newlines
            updateTable(row.index, column.id, numbers.join('\n'), setValue);
        } else if (numbers.length === 1) {
            // Single number paste
            updateTable(row.index, column.id, numbers[0], setValue);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter') {
            onBlur();
            e.preventDefault();
            addNewRow();
        }
        if (
            e.key === 'Delete' &&
            value === '' &&
            table.options.data?.length > 1
        ) {
            e.preventDefault();
            const newIndex = row.index > 0 ? row.index - 1 : 0;
            deleteRow(row.index);
            setFocusedRow(newIndex);
        }
        if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
            setNumberCopy(true);
        }
    };

    const handleChangeNumber = (phoneOrEvent) => {
        let phone;
        if (numberCopy) return;

        // Handle both PhoneInput (direct string) and Chakra Input (event object)
        if (phoneOrEvent?.target) {
            phone = phoneOrEvent.target.value;
        } else {
            phone = phoneOrEvent;
        }

        // Ensure '+' prefix when not in paste mode
        if (!numberCopy && phone && phone.indexOf('+') !== 0) {
            phone = '+' + phone.replace(/\+/g, '');
        }

        // Strip non-numeric characters (preserve delimiters for paste parsing)
        const number = phone.replace(/[^\d\s\n;:,\n]/g, '');
        updateTable(row.index, column.id, number, setValue);

        // Fix cursor position - move to end after update to prevent jumping to start
        if (number.length >= 1 && numberRef.current) {
            setTimeout(() => {
                const input = numberRef.current;
                if (input) {
                    const length = input.value?.length || 0;
                    input.setSelectionRange(length, length);
                }
            }, 0);
        }
    };

    useEffect(() => {
        if (numberRef?.current) return;
        if (row.index === focusedRow) {
            numberRef?.current?.focus();
        }
    }, [focusedRow, row.index, numberRef]);

    useEffect(() => {
        setValue(initValue);
    }, [initValue]);

    useEffect(() => {
        if (row.index === 0) {
            setFocusedRow(0);
        } else {
            setFocusedRow(table.options.data.length - 1);
        }
    }, [row.index, setFocusedRow, table]);

    return (
        <Box
            className={`${spin ? 'animate-[pulse_0.5s_cubic-bezier(0.4,0,0.6,1)_infinite]' : ''}`}
            onPaste={onPaste}
            sx={{
                '& .react-tel-input .selected-flag:hover, .selected-flag:focus':
                    {
                        backgroundColor: 'transparent',
                        cursor: 'auto',
                    },
                '& .react-tel-input .selected-flag': {
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: '12px',
                },
            }}>
            <PhoneInput
                value={value}
                disableDropdown
                onlyCountries={undefined}
                country=""
                name="number"
                renderStringAsFlag={_renderStringAsFlag(value)}
                handleChangeNumber={handleChangeNumber}
                handleBlur={onBlur}
                inputProps={{
                    name: 'number',
                    placeholder: 'For example 44123456789',
                    'aria-label': `${type}-${column.id}-input-${row.index}`,
                    onKeyDown: onKeyDown,
                    ref: numberRef,
                    autoFocus: row.index === focusedRow,
                }}
                inputStyle={{
                    width: '100%',
                    borderRadius: 0,
                    border: 'none',
                    borderBottom: `1px solid ${darkMode ? '#4A5568' : '#E2E8F0'}`,
                    backgroundColor: 'transparent',
                    color: darkMode ? 'white' : 'black',
                    fontSize: '0.875rem',
                    paddingRight: '2rem',
                }}
                buttonStyle={{
                    border: 'none',
                    borderBottom: `1px solid ${darkMode ? '#4A5568' : '#E2E8F0'}`,
                    backgroundColor: 'transparent',
                }}
                dropdownStyle={{
                    backgroundColor: darkMode ? '#2D3748' : 'white',
                    color: darkMode ? 'white' : 'black',
                }}
            />
        </Box>
    );
};

export default PhoneInputCell;
